import React, { useCallback, useState } from 'react';
import { Label } from '@/components/ui/label';
import { ChevronDown, Check, Search, X } from "lucide-react";

interface BusinessActivityInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { business_activity?: string | null; nature_of_business?: string | null };
  onChange?: (val: { business_activity: string }) => void;
  disabled?: boolean;
  registerSubmit?: (fn: () => Promise<{ isValid: boolean; error?: string }>) => void;
  // Legacy pattern
  initialData?: { business_activity?: string };
  onDataChange?: (data: { business_activity: string }) => void;
}

const BUSINESS_ACTIVITY_OPTIONS = [
  "Kirana",
  "Grocery",
  "Restaurant",
  "Apparel",
  "Fast Food and QSR",
  "Pharmacy",
  "Foodcourt",
  "Bakery",
  "Mobile Accessories",
  "Pan Shop",
  "Meat Shop",
  "Coffee and Tea Shop",
  "Salon and Parlour",
  "Sweet Shop",
  "Other Services",
  "Street Hawker",
  "Hardware and Industrial Products",
  "Automobile Parts and Accessories",
  "Telecom",
  "Supermarket",
  "Food Truck",
  "Automobile Repairs and Services",
  "Electrical Shop",
];

const BusinessActivityInput: React.FC<BusinessActivityInputProps> = ({
  value,
  onChange,
  disabled,
  registerSubmit,
  initialData,
  onDataChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string>("");

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const currentValue = (value?.business_activity ?? initialData?.business_activity ?? '') || '';

  const handleChange = useCallback((val: string) => {
    onChange?.({ business_activity: val });
    onDataChange?.({ business_activity: val });
    setError("");
  }, [onChange, onDataChange]);

  const filtered = BUSINESS_ACTIVITY_OPTIONS.filter((a) => a.toLowerCase().includes(search.toLowerCase()));

  // Close when tapping anywhere outside the field/list.
  React.useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const openList = () => {
    if (disabled) return;
    setSearch("");
    setIsOpen(true);
    // Scroll the field near the top so the list stays visible once the
    // soft keyboard opens, then focus so the user can type immediately.
    requestAnimationFrame(() => {
      wrapperRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      searchInputRef.current?.focus();
    });
  };

  const selectActivity = (activity: string) => {
    handleChange(activity);
    setIsOpen(false);
    setSearch("");
  };

  // Register validation with parent
  React.useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        if (!currentValue || currentValue.trim() === '') {
          setError("Please select a business activity");
          return { isValid: false, error: "Please select a business activity" };
        }
        setError("");
        return { isValid: true };
      });
    }
  }, [registerSubmit, currentValue]);

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <Label className="text-gray-500 font-medium">
        Business Activity <span className="text-red-500">*</span>
      </Label>

      {/*
        Inline searchable dropdown (no bottom sheet).
        Closed: looks like a normal select field showing the chosen value.
        Open:   the SAME field becomes the search box and the list renders
                directly underneath it, so the options stay right where the
                user tapped and sit above the soft keyboard.
      */}
      <div className="relative w-full">
        {isOpen ? (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            {/*
              text-base (16px) on mobile is REQUIRED: iOS Safari auto-zooms the
              page whenever a focused input's font-size is under 16px. Drops to
              text-sm from md up, mirroring the shadcn Input component.
            */}
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type to search..."
              disabled={disabled}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              className="flex h-12 w-full rounded-xl border border-intellend-green bg-background pl-9 pr-9 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-intellend-green"
            />
            <button
              type="button"
              onClick={() => { setIsOpen(false); setSearch(""); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={openList}
            className={`flex h-12 w-full min-w-0 items-center justify-between rounded-xl border ${error ? 'border-red-500' : 'border-gray-200'} bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-intellend-green focus:ring-offset-2 disabled:opacity-50`}
          >
            <span className={`truncate ${currentValue ? "text-foreground" : "text-muted-foreground"}`}>
              {currentValue || "Select Business Activity"}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </button>
        )}

        {isOpen && (
          <div className="mt-1 rounded-xl border border-gray-200 bg-white shadow-lg overflow-y-auto overscroll-contain max-h-64">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">No matching activity</p>
            ) : (
              filtered.map((activity) => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => selectActivity(activity)}
                  className={`flex w-full items-center gap-3 px-3 py-3 text-sm text-left ${currentValue === activity ? "bg-intellend-green/5 text-intellend-green font-medium" : "hover:bg-muted"}`}
                >
                  <span className="flex-1">{activity}</span>
                  {currentValue === activity && <Check className="h-4 w-4 text-intellend-green shrink-0" />}
                </button>
              ))
            )}
          </div>
        )}

        {error && !isOpen && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>
    </div>
  );
};

export default BusinessActivityInput;
